import camelCase from 'lodash/camelCase'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import type { CamelCase, KebabCase, Split } from 'type-fest'

import { getCSSVariableValue } from '../utils/css'
import type { ThemeColor } from '../utils/theme'
import { THEME_COLORS } from '../utils/theme'

/**
 * Loads CSS variables from the DOM.
 */
export const useCSSVariables = <T extends Record<string, string>>(
    config: T,
    targetSelector?: string
): { [K in keyof T]: string } | undefined => {
    const router = useRouter()
    const [variables, setVariables] = useState<T>()

    useEffect(() => {
        const result = {}
        const target = targetSelector
            ? document.querySelector(targetSelector)
            : undefined
        for (const [key, value] of Object.entries(config)) {
            result[key] = getCSSVariableValue(value, target as HTMLElement)
        }
        setVariables(result as T)
    }, [router.asPath])

    return variables
}

type RemovePrefix<T extends string> = Split<T, 'theme'>[1]
type ColorKey<T extends ThemeColor = ThemeColor> = CamelCase<RemovePrefix<T>>
type ColorValue<T extends ColorKey = ColorKey> = `theme-${KebabCase<T>}`

export type ThemeColors = { [K in ColorKey]: ColorValue<K> }

/**
 * Loads color variables from the DOM.
 */
export const useColorVariables = () => useCSSVariables(colorMap)
const colorMap = Object.fromEntries(
    THEME_COLORS.map((key) => [camelCase(key.replace('theme', '')), key])
) as ThemeColors
